import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Wand2, X } from 'lucide-react';
import IconTabs, { tabs } from './hero/IconTabs';
import VideoBento from './hero/VideoBento';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderBadge from './AnimatedBorderBadge';
import FeaturesHeroBento from '@/components/features/FeaturesHeroBento';
import GridDots from '@/components/animations/GridDots';

const EditorialHero: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Auto-cycle through tabs
  useEffect(() => {
    const startInterval = () => {
      intervalRef.current = window.setInterval(() => {
        setVideoProgress((prev) => {
          if (prev >= 100) {
            setActiveTab((currentTab) => (currentTab + 1) % tabs.length);
            return 0;
          }
          return prev + 2;
        });
      }, 100);
    };

    startInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    setVideoProgress(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = window.setInterval(() => {
      setVideoProgress((prev) => {
        if (prev >= 100) {
          setActiveTab((currentTab) => (currentTab + 1) % tabs.length);
          return 0;
        }
        return prev + 2;
      });
    }, 100);
  };

  const handleWatchDemo = () => {
    setShowVideoModal(true);
  };

  const handleCloseModal = () => {
    setShowVideoModal(false);
  };

  return (
    <section 
      ref={sectionRef}
      className="relative overflow-hidden min-h-screen pt-32 lg:pt-60 pb-8 transition-colors duration-300"
      style={{ 
        background: theme === 'dark' 
          ? '#111111' 
          : 'linear-gradient(to bottom, #FFFFFF 0%, #f4f2f1 100%)'
      }}
    >
      <GridDots containerRef={sectionRef} />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 lg:px-8">
        {/* Centered Text Content */}
        <div className="flex flex-col items-center text-center space-y-6 max-w-3xl mx-auto mb-12">
          {/* Customer Relationship Magic Badge */}
          <AnimatedBorderBadge text="Customer Relationship Magic" icon={Wand2} />
          <h1 
            className="text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight transition-colors duration-300"
            style={{ 
              background: theme === 'dark' 
                ? 'linear-gradient(to bottom, #FFFFFF 0%, #666666 100%)' 
                : 'linear-gradient(to bottom, #111111 0%, #999999 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Automate Your Marketing.<br />Grow Your Business.
          </h1>

          <p 
            className="text-sm leading-relaxed max-w-lg lg:max-w-2xl transition-colors duration-300" 
            style={{ color: theme === 'dark' ? '#AAAAAA' : '#555555' }}
          >
            Replace 10+ Expensive Tools With One AI-Powered System.<br />Introducing The Complete Business OS for Professionals.
          </p>


          <div className="pt-2 flex justify-center">
            <button 
              onClick={() => navigate('/pricing#pricing-section')}
              className="w-[140px] px-4 py-2 bg-black text-white text-sm font-medium hover:bg-[#222222] hover:scale-[1.02] hover:shadow-md active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 rounded-lg"
            >
              Start for free →
            </button>
          </div>
        </div>

        {/* Mobile-only: Compact FeaturesHeroBento animation */}
        <div className="block sm:hidden max-w-6xl mx-auto w-full overflow-visible -mx-4">
          <div className="h-[320px]">
            <FeaturesHeroBento />
          </div>
        </div>

        {/* Tablet/Desktop: Original Interactive Video Demo */}
        <div className="hidden sm:block max-w-6xl mx-auto w-full">
          <div className="relative">
            <div 
              className="p-4 transition-colors duration-300"
              style={{ 
                backgroundColor: theme === 'dark' ? '#1A1A1A' : '#FFFFFF',
                border: `1px solid ${theme === 'dark' ? '#333333' : '#E0E0E0'}`, 
                borderRadius: '16px',
              }}
            >
              <IconTabs 
                activeTab={activeTab} 
                videoProgress={videoProgress} 
                onTabClick={handleTabClick} 
              />
              
              {/* Horizontal divider line */}
              <div 
                className="h-px w-full mb-4"
                style={{ 
                  backgroundColor: theme === 'dark' ? '#333333' : '#E0E0E0'
                }}
              />
              
              <VideoBento activeTab={activeTab} />
            </div>
            
            {/* White fade overlay - positioned inside border radius */}
            <div 
              className="absolute bottom-[17px] left-[17px] right-[17px] h-24 pointer-events-none"
              style={{
                background: theme === 'dark'
                  ? 'linear-gradient(to top, rgba(26,26,26,1) 0%, rgba(26,26,26,0) 100%)'
                  : 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: '12px',
              }}
            />
          </div>
          
          {/* Integration badge - below bento box */}
          <div className="flex justify-center mt-12 -mb-4">
            <AnimatedBorderBadge text="Integrate all your favorite apps" />
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={handleCloseModal}
        >
          <div 
            className="relative w-full max-w-4xl mx-4 bg-black rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Video */}
            <video
              autoPlay
              controls
              className="w-full aspect-video"
              src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Explainer%20V6%20-%20Complete%20Overview%20Explainer.mp4"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default EditorialHero;
